import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { X, Plus, Trash, CalendarBlank, Tag, CircleNotch, CheckCircle, Clock, Warning } from '@phosphor-icons/react';
import feesService from '../services/feesService';
import DialogModal from './DialogModal';
import LockedFeature from './LockedFeature';
import './FeeDetailsModal.css';

const DISCOUNT_TYPES = [
  { value: 'sibling', label: 'Sibling Discount' },
  { value: 'staff_ward', label: 'Staff Ward Discount' },
  { value: 'scholarship', label: 'Scholarship' },
  { value: 'hardship', label: 'Hardship' },
  { value: 'promotional', label: 'Promotional' },
  { value: 'other', label: 'Other' },
];

function formatCurrency(amount) {
  const numAmount = parseFloat(amount) || 0;
  return `GHS ${numAmount.toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function addDays(dateStr, days) {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

function InstallmentPlanBuilder({ fee, onCreated, onCancel }) {
  const today = new Date().toISOString().split('T')[0];
  const [rows, setRows] = useState([{ amount_due: '', due_date: addDays(today, 30) }]);
  const [submitting, setSubmitting] = useState(false);

  const total = rows.reduce((sum, r) => sum + (parseFloat(r.amount_due) || 0), 0);
  const remaining = Math.round((fee.amount_due - total) * 100) / 100;

  const splitEvenly = (n) => {
    const base = Math.floor((fee.amount_due / n) * 100) / 100;
    const newRows = [];
    let allocated = 0;
    for (let i = 0; i < n; i++) {
      const isLast = i === n - 1;
      const amount = isLast ? Math.round((fee.amount_due - allocated) * 100) / 100 : base;
      allocated += amount;
      newRows.push({ amount_due: amount.toFixed(2), due_date: addDays(today, 30 * (i + 1)) });
    }
    setRows(newRows);
  };

  const updateRow = (index, field, value) => {
    setRows(prev => prev.map((r, i) => i === index ? { ...r, [field]: value } : r));
  };

  const addRow = () => setRows(prev => [...prev, { amount_due: '', due_date: addDays(today, 30 * (prev.length + 1)) }]);
  const removeRow = (index) => setRows(prev => prev.filter((_, i) => i !== index));

  const handleSave = async () => {
    if (Math.abs(remaining) > 0.01) {
      toast.error(`Installments must sum to ${formatCurrency(fee.amount_due)} (currently ${formatCurrency(total)})`);
      return;
    }
    if (rows.some(r => !r.amount_due || !r.due_date)) {
      toast.error('Every installment needs an amount and a due date');
      return;
    }
    setSubmitting(true);
    try {
      await feesService.createInstallmentPlan(fee.id, rows.map(r => ({
        amount_due: parseFloat(r.amount_due),
        due_date: r.due_date
      })));
      toast.success('Installment plan created');
      onCreated();
    } catch (error) {
      toast.error(error.message || 'Failed to create installment plan');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fee-inline-panel">
      <div className="split-shortcuts">
        <span>Quick split:</span>
        {[2, 3, 4].map(n => (
          <button key={n} type="button" className="btn btn-sm btn-secondary" onClick={() => splitEvenly(n)}>
            {n} installments
          </button>
        ))}
      </div>

      <div className="installment-rows">
        {rows.map((row, i) => (
          <div className="installment-row" key={i}>
            <span className="installment-number">#{i + 1}</span>
            <input
              type="number"
              step="0.01"
              min="0.01"
              placeholder="Amount (GHS)"
              value={row.amount_due}
              onChange={(e) => updateRow(i, 'amount_due', e.target.value)}
              disabled={submitting}
            />
            <input
              type="date"
              value={row.due_date}
              onChange={(e) => updateRow(i, 'due_date', e.target.value)}
              disabled={submitting}
            />
            <button
              type="button"
              className="icon-btn danger"
              onClick={() => removeRow(i)}
              disabled={submitting || rows.length === 1}
              title="Remove installment"
            >
              <Trash size={16} />
            </button>
          </div>
        ))}
      </div>

      <button type="button" className="btn btn-sm btn-secondary" onClick={addRow} disabled={submitting}>
        <Plus size={14} /> Add Installment
      </button>

      <div className={`installment-total-row ${Math.abs(remaining) > 0.01 ? 'mismatch' : 'match'}`}>
        <span>Total: {formatCurrency(total)} of {formatCurrency(fee.amount_due)}</span>
        {Math.abs(remaining) > 0.01 && <span>({remaining > 0 ? formatCurrency(remaining) + ' remaining' : formatCurrency(-remaining) + ' over'})</span>}
      </div>

      <div className="fee-inline-panel-actions">
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={submitting}>Cancel</button>
        <button type="button" className="btn btn-primary" onClick={handleSave} disabled={submitting}>
          {submitting ? <CircleNotch size={16} className="spinner" /> : 'Save Plan'}
        </button>
      </div>
    </div>
  );
}

function DiscountForm({ fee, onSaved, onCancel }) {
  const [discount, setDiscount] = useState(fee.discount || '');
  const [discountType, setDiscountType] = useState(fee.discount_type || 'sibling');
  const [reason, setReason] = useState(fee.discount_reason || '');
  const [submitting, setSubmitting] = useState(false);

  const handleSave = async () => {
    const amount = parseFloat(discount);
    if (isNaN(amount) || amount < 0) {
      toast.error('Enter a valid discount amount');
      return;
    }
    if (amount > fee.amount_due) {
      toast.error(`Discount cannot exceed ${formatCurrency(fee.amount_due)}`);
      return;
    }
    setSubmitting(true);
    try {
      await feesService.applyDiscount(fee.id, {
        discount: amount,
        discount_type: discountType,
        discount_reason: reason || null
      });
      toast.success('Discount applied');
      onSaved();
    } catch (error) {
      toast.error(error.message || 'Failed to apply discount');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fee-inline-panel">
      <div className="form-row">
        <div className="form-group">
          <label>Discount Amount (GHS)</label>
          <input
            type="number"
            step="0.01"
            min="0"
            max={fee.amount_due}
            value={discount}
            onChange={(e) => setDiscount(e.target.value)}
            disabled={submitting}
          />
        </div>
        <div className="form-group">
          <label>Discount Type</label>
          <select value={discountType} onChange={(e) => setDiscountType(e.target.value)} disabled={submitting}>
            {DISCOUNT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
      </div>
      <div className="form-group">
        <label>Reason (Optional)</label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g., 2nd child of the Mensah family"
          rows={2}
          disabled={submitting}
        />
      </div>
      <div className="fee-inline-panel-actions">
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={submitting}>Cancel</button>
        <button type="button" className="btn btn-primary" onClick={handleSave} disabled={submitting}>
          {submitting ? <CircleNotch size={16} className="spinner" /> : 'Apply Discount'}
        </button>
      </div>
    </div>
  );
}

const INSTALLMENT_STATUS_ICON = {
  paid: { icon: CheckCircle, color: '#059669' },
  partial: { icon: Clock, color: '#D97706' },
  pending: { icon: Clock, color: '#71717A' },
  overdue: { icon: Warning, color: '#DC2626' },
};

function FeeDetailsModal({ isOpen, onClose, studentId, studentName, onChanged }) {
  const [loading, setLoading] = useState(false);
  const [feesData, setFeesData] = useState(null);
  const [activePanel, setActivePanel] = useState(null); // { feeId, type: 'plan' | 'discount' }
  const [dialogConfig, setDialogConfig] = useState({ isOpen: false });

  const loadFees = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const data = await feesService.getStudentFees(studentId);
      setFeesData(data);
    } catch (error) {
      setDialogConfig({
        isOpen: true,
        title: 'Failed to Load Fees',
        message: error.message || 'Could not load this student\'s fees.',
        type: 'error',
        primaryAction: { label: 'OK', onClick: () => setDialogConfig({ isOpen: false }) }
      });
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    if (isOpen) {
      setActivePanel(null);
      loadFees();
    }
  }, [isOpen, loadFees]);

  if (!isOpen) return null;

  const handleMutationDone = () => {
    setActivePanel(null);
    loadFees();
    if (onChanged) onChanged();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()} data-testid="fee-details-modal">
        <div className="modal-header">
          <h2>Fee Details {studentName ? `— ${studentName}` : ''}</h2>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="fee-details-body">
          {loading ? (
            <div className="loading">Loading fees...</div>
          ) : !feesData || feesData.fees.length === 0 ? (
            <div className="empty-state-card">
              <p>No fees found for this student</p>
            </div>
          ) : (
            feesData.fees.map((fee) => {
              const hasInstallments = fee.installments && fee.installments.length > 0;
              const canSetUpPlan = !hasInstallments && fee.amount_paid === 0;
              const panelOpenForThisFee = activePanel?.feeId === fee.id;

              return (
                <div key={fee.id} className="fee-detail-card">
                  <div className="fee-detail-header">
                    <div>
                      <span className="fee-type-badge">{fee.fee_type}</span>
                      <span className={`status-badge status-${fee.status}`}>{fee.status}</span>
                    </div>
                    <div className="fee-detail-balance">
                      Balance: <strong>{formatCurrency(fee.balance)}</strong>
                    </div>
                  </div>

                  <div className="fee-detail-amounts">
                    <span>Due: {formatCurrency(fee.amount_due)}</span>
                    <span>Paid: {formatCurrency(fee.amount_paid)}</span>
                    {fee.discount > 0 && (
                      <span className="discount-chip">
                        <Tag size={13} /> {formatCurrency(fee.discount)} {fee.discount_type ? `(${fee.discount_type.replace('_', ' ')})` : ''}
                      </span>
                    )}
                  </div>
                  {fee.discount_reason && (
                    <div className="fee-discount-reason">"{fee.discount_reason}"</div>
                  )}

                  {hasInstallments && (
                    <div className="installment-schedule">
                      <div className="installment-schedule-title"><CalendarBlank size={14} /> Installment Plan</div>
                      <table className="installment-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Due Date</th>
                            <th>Amount</th>
                            <th>Paid</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {fee.installments.map((inst) => {
                            const statusMeta = INSTALLMENT_STATUS_ICON[inst.status] || INSTALLMENT_STATUS_ICON.pending;
                            const StatusIcon = statusMeta.icon;
                            return (
                              <tr key={inst.id}>
                                <td>{inst.installment_number}</td>
                                <td>{inst.due_date}</td>
                                <td>{formatCurrency(inst.amount_due)}</td>
                                <td>{formatCurrency(inst.amount_paid)}</td>
                                <td>
                                  <span className="installment-status" style={{ color: statusMeta.color }}>
                                    <StatusIcon size={13} weight="bold" /> {inst.status}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {panelOpenForThisFee && activePanel.type === 'plan' && (
                    <InstallmentPlanBuilder
                      fee={fee}
                      onCreated={handleMutationDone}
                      onCancel={() => setActivePanel(null)}
                    />
                  )}
                  {panelOpenForThisFee && activePanel.type === 'discount' && (
                    <DiscountForm
                      fee={fee}
                      onSaved={handleMutationDone}
                      onCancel={() => setActivePanel(null)}
                    />
                  )}

                  {!panelOpenForThisFee && (
                    <div className="fee-detail-actions">
                      {canSetUpPlan && (
                        <LockedFeature module="fees_plus">
                          <button
                            type="button"
                            className="btn btn-sm btn-secondary"
                            onClick={() => setActivePanel({ feeId: fee.id, type: 'plan' })}
                          >
                            <CalendarBlank size={14} /> Set Up Installment Plan
                          </button>
                        </LockedFeature>
                      )}
                      <LockedFeature module="fees_plus">
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary"
                          onClick={() => setActivePanel({ feeId: fee.id, type: 'discount' })}
                        >
                          <Tag size={14} /> {fee.discount > 0 ? 'Update Discount' : 'Apply Discount'}
                        </button>
                      </LockedFeature>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      <DialogModal
        isOpen={dialogConfig.isOpen}
        onClose={() => setDialogConfig({ isOpen: false })}
        title={dialogConfig.title}
        message={dialogConfig.message}
        type={dialogConfig.type}
        primaryAction={dialogConfig.primaryAction}
      />
    </div>
  );
}

export default FeeDetailsModal;
